import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Current app version - UPDATE THIS WITH EACH RELEASE
const CURRENT_VERSION = '10.0.9';
const VERSION_KEY = 'sentlo_app_version';
const LAST_CHECK_KEY = 'sentlo_last_version_check';

export function VersionManager() {
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    checkVersion();

    // Check for version updates every 5 minutes
    const interval = setInterval(() => {
      checkVersion();
    }, 5 * 60 * 1000); // 5 minutes

    // Also check when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const checkVersion = async () => {
    if (isChecking) return;

    try {
      setIsChecking(true);

      // Get stored version
      const storedVersion = localStorage.getItem(VERSION_KEY);
      const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
      const now = Date.now();

      console.log('🔍 Version Check:', {
        current: CURRENT_VERSION,
        stored: storedVersion,
        lastCheck: lastCheck ? new Date(parseInt(lastCheck)).toLocaleTimeString() : 'Never'
      });

      // If version has changed, force update
      if (storedVersion && storedVersion !== CURRENT_VERSION) {
        console.log('🚨 Version mismatch detected! Stored:', storedVersion, 'Current:', CURRENT_VERSION);
        await forceUpdate();
        return;
      }

      // Store current version if not set
      if (!storedVersion) {
        localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
        console.log('✅ Version stored:', CURRENT_VERSION);
      }

      // Check for service worker updates every 30 seconds if recently checked
      if (lastCheck && (now - parseInt(lastCheck)) < 30000) {
        console.log('⏭️ Skipping version check (checked recently)');
        setIsChecking(false);
        return;
      }

      // Update last check time
      localStorage.setItem(LAST_CHECK_KEY, now.toString());

      // Check service worker for updates
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          console.log('🔄 Checking for service worker updates...');
          await registration.update();
        }
      }

      setIsChecking(false);
    } catch (error) {
      console.error('❌ Version check error:', error);
      setIsChecking(false);
    }
  };

  const forceUpdate = async () => {
    console.log('🔄 Force updating app to version', CURRENT_VERSION);

    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log('🗑️ Clearing caches:', cacheNames);
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('🗑️ Unregistering service workers:', registrations.length);
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      }

      // Update version in localStorage
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);

      // Show update message
      toast.success('App Updated!', {
        description: `Sentlo v${CURRENT_VERSION} - Reloading with latest version...`,
        duration: 2000,
      });

      // Force reload from server (bypass cache)
      setTimeout(() => {
        console.log('🔃 Reloading app...');
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('❌ Force update error:', error);
      
      // Fallback: just reload
      toast.info('Updating...', {
        description: 'Reloading app',
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  // This component doesn't render anything
  return null;
}

// Export version for use in other components
export const APP_VERSION = CURRENT_VERSION;
